import { Component } from "react";

/**
 * Top-level error boundary so a React render crash (anywhere in the tree) shows a
 * recovery screen instead of a blank black page. Without this, a single child
 * component throwing during render unmounts the whole app and the customer sees
 * an empty browser window — exactly what was reported on the checkout flow.
 */
export default class AppErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorText: "" };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, errorText: error && error.message ? String(error.message) : "Unknown error" };
    }

    componentDidCatch(error, info) {
        // Best-effort logging so we can see what crashed in the customer's console.
        try {
            console.error("[Tresses] Uncaught render error:", error, info);
        } catch { /* noop */ }
    }

    handleReload = () => {
        try { window.location.assign("/"); } catch { window.location.reload(); }
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FBF6F0] px-6" data-testid="app-error-fallback">
                <div className="max-w-md w-full bg-[#FDF0EC] border border-[#C29A5B]/40 p-8 text-center">
                    <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Something went wrong</div>
                    <h2 className="font-serif-display text-3xl text-[#3D0C1F] mt-3">We hit a snag.</h2>
                    <p className="text-sm text-[#7A5262] mt-3 leading-relaxed">
                        Your booking may have still been placed — please check <span className="text-[#8F622C]">My Dashboard</span> after returning home. If a booking is missing, our team will be in touch shortly.
                    </p>
                    {this.state.errorText && (
                        <p className="text-[0.65rem] tracking-[0.14em] uppercase text-[#7A5262]/70 mt-4 break-words">
                            {this.state.errorText}
                        </p>
                    )}
                    <div className="mt-6 flex flex-col gap-3">
                        <button
                            onClick={this.handleReload}
                            data-testid="app-error-go-home"
                            className="px-5 py-3 bg-[#6B1535] text-[#FFF8F2] text-[0.7rem] tracking-[0.22em] uppercase hover:bg-[#8B2346] transition-colors"
                        >
                            Return Home
                        </button>
                        <a
                            href="/dashboard"
                            data-testid="app-error-go-dashboard"
                            className="px-5 py-3 border border-[#C29A5B]/40 text-[#8F622C] text-[0.7rem] tracking-[0.22em] uppercase hover:bg-[#6B1535]/10 transition-colors"
                        >
                            Go to My Dashboard
                        </a>
                    </div>
                </div>
            </div>
        );
    }
}
